import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { usePermissionAny } from '../../hooks';
import HaventPermission from '../../assets/HaventPermission';
import { THEMATIC_PLANNING_ACCESS } from '../../utils/const';
import usePageWidth from '../shared/usePageWidth';
import LessonModal from './LessonModal';
import styles from './styles.module.css';

const hasChildren = (item) => Array.isArray(item.children) && item.children.length > 0;

// тип узла — по полю type ответа (semester | section/theme | lesson_plan | control),
// со страховкой по составу полей на случай иного набора значений
const nodeKind = (item) => {
    const type = String(item.type || '').toLowerCase();
    if (type === 'semester') return 'semester';
    if (type === 'lesson_plan' || type === 'lesson') return 'lesson';
    if (type === 'control' || type === 'intermediate_control' || type === 'final_control') return 'control';
    if (type === 'section' || type === 'theme' || type === 'topic') return 'branch';
    if (hasChildren(item)) return 'branch';
    if (item.lesson_number != null || item.lesson_type != null) return 'lesson';
    return 'branch';
};

// число course_tema_detail.id из id узла-занятия ("lesson_152" → 152)
const lessonIdOf = (item) => {
    const m = /^lesson[_-]?(\d+)$/i.exec(String(item.id ?? ''));
    if (m) return m[1];
    if (item.lesson_id != null) return String(item.lesson_id);
    if (typeof item.id === 'number') return String(item.id);
    return null;
};

const hoursCell = (item) => {
    const fact = item.fact_hours;
    const plan = item.plan_hours;
    if (plan == null) {
        const v = item.hours ?? fact;
        return { display: v == null ? '-' : v, title: undefined };
    }
    if (fact === plan) return { display: fact, title: undefined };
    return {
        display: `${fact ?? '-'} (${plan})`,
        title: `Фактических часов: ${fact ?? '-'}, плановых: ${plan}`,
    };
};

const TopicsView = ({
    id,
    tree,
    isLoadingTree,
    treeError,
    fetchThematicPlanningTree,
    clearThematicPlanningTree,

    lesson,
    isLoadingLesson,
    lessonError,
    fetchThematicPlanningLesson,
    clearThematicPlanningLesson,
    openLessonFile,

    history,
    location,
}) => {
    usePageWidth('pageWide');

    const { readRight } = usePermissionAny(THEMATIC_PLANNING_ACCESS);

    const [openLessonId, setOpenLessonId] = useState(null);

    useEffect(() => {
        if (readRight && id) fetchThematicPlanningTree(id);
        return () => clearThematicPlanningTree();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, readRight]);

    const plan = location?.state?.plan || {};

    const handleBack = () => history.push('/topics');

    const openLesson = (lessonId) => {
        if (!lessonId) return;
        setOpenLessonId(lessonId);
        fetchThematicPlanningLesson(lessonId);
    };

    const closeLesson = () => {
        setOpenLessonId(null);
        clearThematicPlanningLesson();
    };

    if (!readRight) return <HaventPermission />;

    const renderRows = (item, depth, key) => {
        const kind = nodeKind(item);
        const branch = hasChildren(item);
        const rowClass = [
            kind === 'semester' ? styles.rowSemester : '',
            kind === 'branch' ? styles.rowBranch : '',
            kind === 'control' ? styles.rowControl : '',
            kind === 'lesson' ? styles.rowLesson : '',
        ]
            .filter(Boolean)
            .join(' ');

        const { display, title } = hoursCell(item);
        const lessonId = kind === 'lesson' ? lessonIdOf(item) : null;

        const row = (
            <tr
                key={key}
                className={rowClass}
                onClick={kind === 'lesson' ? () => openLesson(lessonId) : undefined}
            >
                <td className={styles.colNum}>{kind === 'lesson' ? item.lesson_number ?? '' : ''}</td>
                <td className={styles.colType}>{kind === 'lesson' ? item.lesson_type ?? '' : ''}</td>
                <td className={styles.colHours} title={title}>
                    {display}
                </td>
                <td className={styles.colTitle} style={{ paddingLeft: `${1 + depth * 1.25}rem` }}>
                    {item.title}
                </td>
            </tr>
        );

        const childRows = branch
            ? item.children.flatMap((child, index) => renderRows(child, depth + 1, `${key}-${index}`))
            : [];

        return [row, ...childRows];
    };

    const items = tree?.items ?? [];

    return (
        <div className={styles.page}>
            <div className={styles.cardHead}>
                <button type="button" className={styles.backBtn} onClick={handleBack}>
                    ← Назад к списку
                </button>
            </div>

            {isLoadingTree ? (
                <div className={styles.loader}>
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </div>
            ) : treeError ? (
                <div className={styles.error}>
                    <div className={styles.errorTitle}>Не удалось загрузить перечень занятий</div>
                    {treeError === 403
                        ? 'Нет доступа к тематическому плану'
                        : treeError === 404
                        ? 'Тематический план не найден'
                        : 'Сервер вернул ошибку'}
                </div>
            ) : (
                <>
                    {(plan.name || plan.course_name) && (
                        <>
                            <div className={styles.head}>
                                <h1 className={styles.title}>{plan.name || plan.course_name}</h1>
                            </div>
                            <div className={styles.subline}>
                                {plan.course_name && (
                                    <>
                                        <b>Дисциплина РПД:</b> {plan.course_name}
                                    </>
                                )}
                                {plan.curriculum && (
                                    <>
                                        {' '}&nbsp;·&nbsp; <b>Учебный план:</b> {plan.curriculum}
                                    </>
                                )}
                                {plan.status && (
                                    <>
                                        {' '}&nbsp;·&nbsp; <b>Статус:</b> {plan.status}
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {items.length === 0 ? (
                        <div className={styles.empty}>
                            <div className={styles.emptyTitle}>Тематический план пуст</div>
                            В плане нет разделов и занятий
                        </div>
                    ) : (
                        <div className={styles.tableWrap}>
                            <table className={styles.treeTable}>
                                <thead>
                                    <tr>
                                        <th className={styles.colNum}>№ п/п</th>
                                        <th className={styles.colType}>Вид занятий</th>
                                        <th className={styles.colHours}>Кол-во часов</th>
                                        <th className={styles.colTitle}>Название</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.flatMap((item, index) => renderRows(item, 0, `${item.id ?? index}`))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {openLessonId && (
                <LessonModal
                    lesson={lesson}
                    isLoading={isLoadingLesson}
                    error={lessonError}
                    onClose={closeLesson}
                    onOpenFile={(fileId, opts) => openLessonFile(openLessonId, fileId, opts)}
                />
            )}
        </div>
    );
};

export default TopicsView;
